/**
 * The peg solitaire rules, ported from `apps/puzzletypes/peg_solitaire/board.py`. The two are
 * kept at parity by the shared fixtures in `shared/fixtures/peg_solitaire/`.
 *
 * Pure: it reads rows and jumps and returns positions. It knows nothing of React, of the core,
 * or of the solution.
 */
import { EMPTY, NO_HOLE, PEG, type Cell, type Move } from "./types";

const DIRECTIONS: Cell[] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

/** The classic English board: 33 holes, the centre one empty. */
export const ENGLISH_CROSS = [
  "  ooo  ",
  "  ooo  ",
  "ooooooo",
  "ooo.ooo",
  "ooooooo",
  "  ooo  ",
  "  ooo  ",
];

/** Cells live in sets, so they travel as `row,col` keys. */
export function key(row: number, col: number): string {
  return `${row},${col}`;
}

export function fromKey(value: string): Cell {
  const [row, col] = value.split(",").map(Number);
  return [row, col];
}

export interface Board {
  rows: number;
  cols: number;
  holes: Set<string>;
}

/** Read rows into a board and the pegs it starts with. Unknown characters are no hole. */
export function parseBoard(rows: string[]): { board: Board; pegs: Set<string> } {
  const holes = new Set<string>();
  const pegs = new Set<string>();
  rows.forEach((row, r) =>
    Array.from(row).forEach((char, c) => {
      if (char === PEG || char === EMPTY) holes.add(key(r, c));
      if (char === PEG) pegs.add(key(r, c));
    }),
  );
  const cols = rows.reduce((max, row) => Math.max(max, row.length), 0);
  return { board: { rows: rows.length, cols, holes }, pegs };
}

/** A jump read from JSON, or `null` when it is not four integers. */
export function asMove(raw: unknown): Move | null {
  if (!Array.isArray(raw) || raw.length !== 4) return null;
  if (!raw.every((value) => Number.isInteger(value))) return null;
  return [raw[0], raw[1], raw[2], raw[3]] as Move;
}

/** The pegs after one jump, or `null` when the rules forbid it. */
export function jump(board: Board, pegs: Set<string>, move: Move): Set<string> | null {
  const [fr, fc, tr, tc] = move;
  const dr = tr - fr;
  const dc = tc - fc;
  const straight = (Math.abs(dr) === 2 && dc === 0) || (dr === 0 && Math.abs(dc) === 2);
  if (!straight) return null;
  const over = key(fr + dr / 2, fc + dc / 2);
  const to = key(tr, tc);
  if (!pegs.has(key(fr, fc)) || !pegs.has(over)) return null;
  if (!board.holes.has(to) || pegs.has(to)) return null;
  const next = new Set(pegs);
  next.delete(key(fr, fc));
  next.delete(over);
  next.add(to);
  return next;
}

/** The jumps a peg can make from `[row, col]`. */
export function jumpsFrom(board: Board, pegs: Set<string>, row: number, col: number): Move[] {
  return DIRECTIONS.map(([dr, dc]) => [row, col, row + 2 * dr, col + 2 * dc] as Move).filter(
    (move) => jump(board, pegs, move) !== null,
  );
}

/** Every jump the position allows, in reading order. */
export function legalJumps(board: Board, pegs: Set<string>): Move[] {
  return [...pegs]
    .map(fromKey)
    .sort((a, b) => a[0] - b[0] || a[1] - b[1])
    .flatMap(([r, c]) => jumpsFrom(board, pegs, r, c));
}

export interface Replay {
  pegs: Set<string>;
  /** The jumps that were played, up to the first illegal one. */
  moves: Move[];
  /** Index of the first jump that could not be played, or `null` when all were legal. */
  illegalAt: number | null;
}

/** Play the jumps from `start`, stopping at the first illegal one. */
export function replay(board: Board, start: Set<string>, moves: readonly unknown[]): Replay {
  let pegs = start;
  const played: Move[] = [];
  for (let index = 0; index < moves.length; index += 1) {
    const move = asMove(moves[index]);
    const after = move ? jump(board, pegs, move) : null;
    if (!move || !after) return { pegs, moves: played, illegalAt: index };
    pegs = after;
    played.push(move);
  }
  return { pegs, moves: played, illegalAt: null };
}

/** One peg left — on the target, when the puzzle names one. */
export function isWon(pegs: Set<string>, target: Cell | null | undefined): boolean {
  if (pegs.size !== 1) return false;
  return !target || pegs.has(key(target[0], target[1]));
}

/** More than one peg and no jump left: the position is lost, whatever comes next. */
export function isStuck(board: Board, pegs: Set<string>): boolean {
  return pegs.size > 1 && legalJumps(board, pegs).length === 0;
}

/** The board as rows again, with the pegs where they now are. */
export function render(board: Board, pegs: Set<string>): string[] {
  const rows: string[] = [];
  for (let r = 0; r < board.rows; r += 1) {
    let row = "";
    for (let c = 0; c < board.cols; c += 1) {
      const id = key(r, c);
      row += !board.holes.has(id) ? NO_HOLE : pegs.has(id) ? PEG : EMPTY;
    }
    rows.push(row);
  }
  return rows;
}

export function sameMove(a: Move, b: Move): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
}
